import type { CompanyArticleDetailsRedirect } from "@/modules/companies/api/endpoints/companyArticleDetails"
import type { CompanyArticleDetails } from "@/modules/companies/api/types"

import { useQuery } from "@tanstack/react-query"
import { useParams } from "next/navigation"

import {
  DEFAULT_QUERY_CACHE_TIME as QUERY_CACHE_TIME,
  DEFAULT_QUERY_STALE_TIME as QUERY_STALE_TIME,
} from "@/modules/api/constants"
import {
  COMPANY_ARTICLE_DETAILS_QUERY_KEY,
  getCompanyArticleDetails,
} from "@/modules/companies/api/endpoints/companyArticleDetails"
import { COMPANY_NEWSFEED } from "@/modules/companies/utils/routing"
import { useLocale } from "@/modules/locales/useLocale"
import { useRouter } from "@/modules/routing/hooks/useRouter"

export type CompanyArticleDetailsFetch = {
  dataCompanyArticleDetails?: CompanyArticleDetails
  isErrorCompanyArticleDetails: boolean
  isLoadingCompanyArticleDetails: boolean
  isSuccessCompanyArticleDetails: boolean
}

export const useCompanyArticleDetailsFetch = (): CompanyArticleDetailsFetch => {
  const { replace } = useRouter()
  const { articleSlug, companySlug } = useParams()
  const locale = useLocale()

  const {
    data,
    isError: isErrorCompanyArticleDetails,
    isPending: isLoadingCompanyArticleDetails,
    isSuccess: isSuccessCompanyArticleDetails,
  } = useQuery({
    enabled: !!articleSlug,
    gcTime: QUERY_CACHE_TIME,
    queryFn: () => getCompanyArticleDetails({ article_slug: articleSlug as string }),
    queryKey: [COMPANY_ARTICLE_DETAILS_QUERY_KEY, articleSlug],
    staleTime: QUERY_STALE_TIME,
  })

  if (data && (data as CompanyArticleDetailsRedirect).responseType === "redirect") {
    replace(
      `/${locale}/companies/${companySlug}/${COMPANY_NEWSFEED}/${(data as CompanyArticleDetailsRedirect).newPermalink}`
    )
  }

  return {
    dataCompanyArticleDetails: data ? (data as CompanyArticleDetails) : undefined,
    isErrorCompanyArticleDetails,
    isLoadingCompanyArticleDetails,
    isSuccessCompanyArticleDetails,
  }
}
